import React, { useEffect, useRef, useState, useCallback } from 'react';
import RunStatus from './RunStatus';
import RunHistory from './RunHistory';

const TERMINAL = ['completed', 'failed', 'timeout'];

/**
 * Farm selector + trigger button. Polls the active run until it reaches a terminal
 * state, then refreshes history.
 */
export default function FarmRunner({ api, pollIntervalMs, initialFarm }) {
  const [farms, setFarms] = useState([]);
  const [farm, setFarm] = useState(initialFarm || '');
  const [loadingFarms, setLoadingFarms] = useState(true);
  const [activeRun, setActiveRun] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const [triggering, setTriggering] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    api
      .listFarms()
      .then((res) => {
        if (!mounted) return;
        setFarms(res.farms || []);
        if (!initialFarm && res.farms && res.farms.length) {
          setFarm(res.farms[0].code);
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => mounted && setLoadingFarms(false));
    return () => {
      mounted = false;
    };
  }, [api, initialFarm]);

  const loadHistory = useCallback(
    (code) => {
      if (!code) return;
      api
        .listRuns(code)
        .then((res) => setHistory(res.runs || []))
        .catch((e) => setError(e.message));
    },
    [api]
  );

  // Load history whenever the selected farm changes.
  useEffect(() => {
    setActiveRun(null);
    loadHistory(farm);
  }, [farm, loadHistory]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => stopPolling, []);

  const startPolling = useCallback(
    (code, runId) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const run = await api.getRun(code, runId);
          setActiveRun(run);
          if (TERMINAL.includes((run.Status || '').toLowerCase())) {
            stopPolling();
            loadHistory(code);
          }
        } catch (e) {
          setError(e.message);
          stopPolling();
        }
      }, pollIntervalMs);
    },
    [api, pollIntervalMs, loadHistory]
  );

  const trigger = async () => {
    setError('');
    setTriggering(true);
    try {
      const res = await api.triggerRun(farm);
      const run = { Farm: res.farm, RunId: res.runId, Status: res.status, StartedAt: res.startedAt };
      setActiveRun(run);
      startPolling(farm, res.runId);
      loadHistory(farm);
    } catch (e) {
      if (e.status === 409 && e.body && e.body.runId) {
        // Already running — attach to the existing run.
        const run = { Farm: farm, RunId: e.body.runId, Status: 'running', StartedAt: e.body.startedAt };
        setActiveRun(run);
        startPolling(farm, e.body.runId);
      }
      setError(e.message);
    } finally {
      setTriggering(false);
    }
  };

  const running = activeRun && (activeRun.Status || '').toLowerCase() === 'running';

  if (loadingFarms) return <div className="card">Loading farms…</div>;

  if (!farms.length) {
    return <div className="card">No farms with stored credentials are available.</div>;
  }

  return (
    <div className="runner">
      <div className="card controls">
        <label htmlFor="farm-select">Farm</label>
        <select
          id="farm-select"
          value={farm}
          onChange={(e) => setFarm(e.target.value)}
          disabled={running}
        >
          {farms.map((f) => (
            <option key={f.code} value={f.code}>
              {f.code}
            </option>
          ))}
        </select>
        <button className="primary" onClick={trigger} disabled={triggering || running}>
          {running ? 'Running…' : triggering ? 'Starting…' : 'Run scenario'}
        </button>
      </div>

      {error && <div className="error card">{error}</div>}

      {activeRun && <RunStatus run={activeRun} />}
      
      {!activeRun && selectedRun && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>Selected Run Details</h3>
            <button onClick={() => setSelectedRun(null)} style={{ padding: '4px 12px', cursor: 'pointer' }}>✕ Close</button>
          </div>
          <RunStatus run={selectedRun} />
        </div>
      )}

      <RunHistory 
        runs={history} 
        selectedRunId={selectedRun?.RunId}
        onSelectRun={setSelectedRun}
      />
    </div>
  );
}
